<script lang="ts">
  /**
   * MobileMonthView - Calendar › Month on phones (plans/mobile-v2.md §3.6): one
   * month as a 7-column grid with the date in a 30px circle, up to three
   * event-type dots per day and thin bars for multi-day events, then the selected
   * day's events below the grid. ‹ › live in the page's top bar; a horizontal
   * swipe on the grid calls the same handlers. The page remounts the view per
   * month, so the selection resets to today / the 1st.
   */
  import { untrack } from 'svelte';
  import type { CalendarEvent } from '../../lib/types';
  import { eventsForDay, getTodayStr, sortEventsForDisplay } from '../../lib/utils';
  import { openEventModal, openEventModalForEdit } from '../../lib/stores/ui';
  import {
    buildMonthGrid,
    defaultSelectedDate,
    formatDayHeading,
    swipeDirection,
  } from '../../lib/mobileCalendar';
  import FabButton from './FabButton.svelte';
  import MobileEventRow from './MobileEventRow.svelte';

  interface Props {
    year: number;
    /** 1–12 */
    month: number;
    events: CalendarEvent[];
    onPrevious: () => void;
    onNext: () => void;
  }

  let { year, month, events, onPrevious, onNext }: Props = $props();

  const DOW = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
  const LANE_PX = 7;

  let today = $derived(getTodayStr());
  let weeks = $derived(buildMonthGrid(year, month, events, today));

  let selected = $state(untrack(() => defaultSelectedDate(year, month)));
  let dayEvents = $derived(sortEventsForDisplay(eventsForDay(events, selected)));

  function cellLabel(cell: { date: string; eventCount: number }): string {
    const n = cell.eventCount;
    return `${formatDayHeading(cell.date)}${n ? `, ${n} event${n === 1 ? '' : 's'}` : ''}`;
  }

  // Horizontal swipe on the grid. Vertical drags stay with the browser (touch-action).
  let swipeStart: { x: number; y: number } | null = null;
  let swiped = false;

  function onPointerDown(event: PointerEvent) {
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    swipeStart = { x: event.clientX, y: event.clientY };
    swiped = false;
  }

  function onPointerUp(event: PointerEvent) {
    if (!swipeStart) return;
    const direction = swipeDirection(event.clientX - swipeStart.x, event.clientY - swipeStart.y);
    swipeStart = null;
    if (!direction) return;
    swiped = true;
    if (direction > 0) onNext();
    else onPrevious();
  }

  // The click that ends a swipe would otherwise select the day under the finger.
  function onClickCapture(event: MouseEvent) {
    if (!swiped) return;
    swiped = false;
    event.stopPropagation();
    event.preventDefault();
  }
</script>

<div class="month-view">
  <div class="grid-wrap">
    <div class="dow" aria-hidden="true">
      {#each DOW as letter, i (i)}<span>{letter}</span>{/each}
    </div>
    <!-- svelte-ignore a11y_no_static_element_interactions -->
    <div
      class="grid"
      role="group"
      aria-label="Month grid"
      onpointerdown={onPointerDown}
      onpointerup={onPointerUp}
      onpointercancel={() => (swipeStart = null)}
      onclickcapture={onClickCapture}
    >
      {#each weeks as week (week.days[0].date)}
        <div class="week">
          <div class="cells">
            {#each week.days as cell (cell.date)}
              <button
                type="button"
                class="cell"
                class:out={!cell.inMonth}
                class:today={cell.isToday}
                class:sel={cell.date === selected}
                aria-label={cellLabel(cell)}
                aria-current={cell.isToday ? 'date' : undefined}
                aria-pressed={cell.date === selected}
                onclick={() => (selected = cell.date)}
              >
                <span class="n">{cell.day}</span>
                <span class="dots" aria-hidden="true">
                  {#each cell.dots as color, j (j)}<i style:background={color}></i>{/each}
                </span>
              </button>
            {/each}
          </div>
          {#if week.laneCount}
            <div class="spans" style:height="{week.laneCount * LANE_PX}px" aria-hidden="true">
              {#each week.spans as span (span.event.id)}
                <i
                  class="span"
                  class:left={span.continuesLeft}
                  class:right={span.continuesRight}
                  style:background={span.color}
                  style:grid-column="{span.startCol + 1} / {span.endCol + 2}"
                  style:grid-row={span.lane + 1}
                ></i>
              {/each}
            </div>
          {/if}
        </div>
      {/each}
    </div>
  </div>

  <section class="day" aria-label="Events on {formatDayHeading(selected)}">
    <div class="day-head">
      <h2>{formatDayHeading(selected)}</h2>
      <button type="button" class="add" onclick={() => openEventModal(selected)}>+ Event</button>
    </div>
    {#each dayEvents as event (event.id)}
      <MobileEventRow {event} onSelect={openEventModalForEdit} />
    {:else}
      <p class="empty">Nothing scheduled</p>
    {/each}
  </section>

  <FabButton label="Add event" onClick={() => openEventModal(selected)} />
</div>

<style>
  .month-view {
    display: flex;
    flex-direction: column;
    height: 100%;
    min-width: 0;
    overflow: hidden;
    background: #f9fafb;
  }

  .grid-wrap {
    flex: none;
    padding: 4px 8px 8px;
    background: #fff;
    border-bottom: 1px solid #e5e7eb;
  }

  .dow,
  .cells {
    display: grid;
    grid-template-columns: repeat(7, minmax(0, 1fr));
  }

  .dow span {
    padding: 4px 0;
    text-align: center;
    font-size: 12px;
    font-weight: 700;
    color: #6b7280;
  }

  .grid {
    touch-action: pan-y;
  }

  .cell {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 3px;
    height: 46px;
    padding: 4px 0 0;
    font: inherit;
    color: #111827;
    background: none;
    border: 0;
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;
  }

  .cell:active .n {
    background: #f3f4f6;
  }

  .n {
    display: grid;
    place-items: center;
    width: 30px;
    height: 30px;
    font-size: 16px;
    font-weight: 500;
    border-radius: 50%;
  }

  .cell.out .n {
    color: #9ca3af;
  }

  .cell.today .n {
    font-weight: 700;
    color: #fff;
    background: #2563eb;
  }

  .cell.sel .n {
    box-shadow: 0 0 0 2px #2563eb;
  }

  .cell.sel.today .n {
    box-shadow:
      inset 0 0 0 2px #fff,
      0 0 0 2px #2563eb;
  }

  .dots {
    display: flex;
    gap: 3px;
    height: 6px;
  }

  .dots i {
    width: 5px;
    height: 5px;
    border-radius: 50%;
  }

  /* Thin bars of multi-day events, one row per lane under the week's cells. */
  .spans {
    display: grid;
    grid-template-columns: repeat(7, minmax(0, 1fr));
    grid-auto-rows: 7px;
    padding-top: 1px;
    pointer-events: none;
  }

  .span {
    align-self: center;
    height: 5px;
    margin: 0 4px;
    border-radius: 3px;
  }

  .span.left {
    margin-left: 0;
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
  }

  .span.right {
    margin-right: 0;
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
  }

  /* Only the selected day's list scrolls; the grid stays put. */
  .day {
    flex: 1;
    min-height: 0;
    padding-bottom: 96px; /* room for the "+" button */
    overflow-y: auto;
    overscroll-behavior: contain;
  }

  .day-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0 16px;
    min-height: 44px;
  }

  .day-head h2 {
    margin: 0;
    font-size: 15px;
    font-weight: 700;
    color: #111827;
  }

  .add {
    min-height: 44px;
    padding: 0 0 0 12px;
    font: inherit;
    font-size: 14px;
    font-weight: 600;
    color: #2563eb;
    background: none;
    border: 0;
    cursor: pointer;
  }

  .empty {
    margin: 0;
    padding: 12px 16px;
    font-size: 14px;
    color: #9ca3af;
    background: #fff;
    border-top: 1px solid #f1f5f9;
  }
</style>
